import { AxiosResponse } from "axios";
import { NewTag } from "components/modals/Add-tag-modal/AddTagModal";
import { uniq } from "lodash";
import toIsoMidday from "utils/to-iso-midday";
import { v4 as uuid } from "uuid";

import { DocumentDataField, DocumentFileDataField } from "../../../constants";
// import { ALL_INVESTORS_VALUE } from "../../../constants/tagger";
import "../../../utils/strings";
import {
  AltiusErrors,
  AltiusRes,
  CurrencyType,
  IFetchTagsParams,
  IProcessedTag,
  ITagAsDraftParams,
  ITagger,
  ITagSplitAsDraftParams,
  Polygons,
  TaggerSplitByGroup,
  TagPhrasePayload
} from "../../types";
import { IEditTagPayload, ITag, ITaggerDocument } from "../../types/tagger/api";
import { TaggerAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

const reqIdToFiles: Record<string, { seriesId: string; fileIndices: number[] }> = {};

export interface ITaggerActions {
  changeDocumentData: (
    docId: ITaggerDocument["id"],
    field: DocumentDataField,
    value: number | string | CurrencyType | null
  ) => void;
  changeDocumentFileData: (
    docId: ITaggerDocument["id"],
    field: DocumentFileDataField,
    value: string | number | null
  ) => void;
  addFiles: (
    files: Array<{ file: File; dealId: number; folderId: number; documentType?: string }>,
    analyze?: boolean
  ) => Promise<ITaggerDocument[]>;
  extractValues: (docId: ITaggerDocument["id"]) => Promise<void>;
  updatePolygons: (result: {
    data: { series: string; doc: string; polygons: Polygons } | null;
    error: string | null;
    reqId: string;
  }) => void;
  tagPhrase: (data: TagPhrasePayload) => Promise<AltiusErrors | void>;
  addTag: (data: {
    tag: NewTag;
    seriesId?: number;
    investor_ids?: number[];
    split_by?: TaggerSplitByGroup;
  }) => Promise<AltiusErrors | void>;
  editTag: (data: IEditTagPayload) => Promise<AltiusErrors | void>;
  deleteTag: (data: { dealId: number; seriesId: number | null; tagId: number }) => void;
  deleteRule: (docId: ITaggerDocument["id"], tagIndex: number, suspect: IProcessedTag) => void;
  deleteSplitOfTags: (data: { dealId: number; seriesId: number | null; splitId: number; investorId?: number }) => void;
  splitTagByCommitment: (dealId: number, seriesId: IProcessedTag["series_id"], tagId: IProcessedTag["id"]) => void;
  splitTagByInvestor: (dealId: number, seriesId: IProcessedTag["series_id"], tagId: number, investorId: number) => void;
  tagAsDraft: (params: ITagAsDraftParams) => void;
  tagSplitAsDraft: (params: ITagSplitAsDraftParams) => void;
  saveTags: (state: ITagger) => Promise<void>;
  getDocumentPolygons: (docId: ITaggerDocument["id"]) => Promise<Polygons>;
  fetchTags: (params: IFetchTagsParams) => Promise<void>;
  resetTagger: () => void;
  // setSuspects: (docId: string, suspects: Suspect[]) => void;
  setAnalyzingStatus: (status: ITagger["analyzingStatus"]) => void;
  startAnalysis: (docIds: number[]) => void;
  undoAnalysis: (docIds: number[]) => void;
}

export class TaggerActions extends AsyncActions implements ITaggerActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public changeDocumentData = (
    docId: ITaggerDocument["id"],
    field: DocumentDataField,
    value: number | string | CurrencyType | null
  ) => {
    this.dispatch({
      type: TaggerAction.ChangeDocumentData,
      payload: { docId, field, value }
    });
  };

  public changeDocumentFileData = (
    docId: ITaggerDocument["id"],
    field: DocumentFileDataField,
    value: string | number | null
  ) => {
    this.dispatch({
      type: TaggerAction.ChangeDocumentFileData,
      payload: { docId, field, value }
    });
  };

  public addFiles = async (
    files: Array<{ file: File; dealId: number; folderId: number; fileId?: number }>,
    analyze: boolean = false
  ) => {
    const reqId = uuid();
    reqIdToFiles[reqId] = { seriesId: "", fileIndices: files.map((_, i) => i) };
    const requestData = {
      deals: files.map((file) => file.dealId),
      file_ids: files.map((file) => file.fileId),
      analyze_with_ai: analyze ? 1 : 0
    };
    const res: AxiosResponse<AltiusRes<ITaggerDocument[]>> = await this.api.post({
      data: requestData,
      url: "/api/v0.0.3/textract/send"
    });
    this.dispatch({
      type: TaggerAction.AddFiles,
      payload: {
        documents: res.data.data,
        files: files.map((file) => file.file),
        folders: files.map((file) => file.folderId)
      }
    });
    const documentDealSeries = res.data.data.map((doc) => ({ dealId: doc.deal_id, seriesId: doc.series_id }));
    const uniqueDealSeries = documentDealSeries.filter(
      (item, index, self) => index === self.findIndex((t) => t.dealId === item.dealId && t.seriesId === item.seriesId)
    );

    await Promise.all(
      uniqueDealSeries.map(({ dealId, seriesId }) => {
        return this.fetchTags({ dealId, seriesId });
      })
    );
    // const seriesIds = uniq(res.data.data.map((doc) => doc.series_id));
    // const dealIds = uniq(files.map((file) => file.dealId));

    // await Promise.all([
    //   ...seriesIds.map((seriesId) => this.fetchTags({ seriesId })),
    //   ...dealIds.map((dealId) => this.fetchTags({ dealId }))
    // ]);

    return res.data.data;
  };

  public extractValues = async (docId: ITaggerDocument["id"]) => {
    await this.api.get({
      url: `/api/v0.0.3/textract/analyze-document/${docId}`
    });
  };

  public updatePolygons = (result: {
    data: { series: string; doc: string; polygons: Polygons } | null;
    error: string | null;
    reqId: string;
  }) => {
    if (!result.error && result.data) {
      this.dispatch({
        type: TaggerAction.UpdatePolygons,
        payload: result.data
      });

      // const instance = axios.create({ baseURL: "https://tagger.local" });
      // instance.post("sendToNLP", { series: result.data.series, doc: result.data.doc });
    } else {
      this.dispatch({
        type: TaggerAction.PolygonsError,
        payload: { error: result.error, ...reqIdToFiles[result.reqId] }
      });
    }
  };

  public saveTags: ITaggerActions["saveTags"] = async (state) => {
    let dealIds = state.tags.map((tag) => tag.deal.id);
    dealIds = uniq(dealIds.filter(Boolean));

    const categorizedFiles = Object.values(state.documents)
      .map((document) => document.fileData)
      .filter((fileData) => fileData.document_type);

    await this.api.post({
      url: "/api/v0.0.3/pdf/tags/save",
      data: {
        deal_ids: dealIds,
        document_ids: state.documentIds,
        categorized_files: categorizedFiles
      }
    });
  };

  public tagPhrase: ITaggerActions["tagPhrase"] = async (data) => {
    const { split_by, is_measure_global_value, investor_id, round_id, ...tagData } = data;

    const tagPayload = {
      ...tagData,
      ...(split_by ? { split_by } : null),
      ...(round_id && !is_measure_global_value ? { round_id } : null),
      ...(investor_id && !is_measure_global_value && split_by !== "all_investors" ? { investor_id } : null)
    };

    const res = await this.api.post<AltiusRes<ITag>>({ url: "/api/v0.0.3/pdf/tags", data: tagPayload });
    res.data.data.series_id &&
      res.data.data.deal.id &&
      this.fetchTags({ seriesId: res.data.data.series_id, dealId: res.data.data.deal.id, overrideStateTags: true });
  };

  public addTag: ITaggerActions["addTag"] = async (data) => {
    const { tag, seriesId, investor_ids, split_by } = data;

    const tagPayload = {
      deal_id: tag.deal.id,
      measure_id: tag.measure.id,
      investor_id: tag.investor?.id,
      round_id: tag.round?.id,
      value: tag.value,
      date: tag.date,
      accounting_date: tag.accounting_date,
      currency: tag.currency,
      description: tag.description,
      split_by: split_by,
      ...(investor_ids && { investor_ids }),
      ...(split_by && { split_by })
    };

    const res = await this.api.post<AltiusRes<ITag>>({ url: "/api/v0.0.3/pdf/tags", data: tagPayload });
    const apiTag = res.data.data;
    this.fetchTags({ dealId: apiTag.deal.id, seriesId: seriesId, overrideStateTags: true });
  };

  public editTag: ITaggerActions["editTag"] = async (data) => {
    const {
      id,
      document_series_id,
      children,
      split_id,
      measure_id,
      date,
      currency,
      description,
      accounting_date,
      is_draft,
      investor_id,
      round_id,
      value,
      is_child
    } = data;

    const isParent = !!children;

    const tagPayload = {
      description,
      ...(measure_id && { measure_id }),
      ...(currency && { currency }),
      ...(date && { date: toIsoMidday(date) }),
      ...(accounting_date && { accounting_date: toIsoMidday(accounting_date) }),
      ...(!is_child && { is_draft }), //we can change draft state only for main parent (single tag or main group)
      ...((is_child || !isParent) && { investor_id }), // if is_child: true, it means we are editing a child group, so we need to provide the investor_id. If we will send investor_id & is_draft it the same payload when we want update measure of group, server will ignore investor_id.
      ...(!isParent && { round_id }),
      ...(!isParent && { value })
    };

    const url = children ? `/api/v0.0.3/pdf/tags/by-split-id/${split_id}` : `/api/v0.0.3/pdf/tags/${id}`;

    const res = await this.api.patch<AltiusRes<ITag>>({ url, data: tagPayload });
    this.fetchTags({ dealId: res.data.data.deal.id, seriesId: document_series_id, overrideStateTags: true });
  };

  public deleteRule = async (docId: ITaggerDocument["id"], tagId: number, suspect: IProcessedTag) => {
    //Client not use it
    const payload = {
      document_id: docId,
      tags: [{ parameter: suspect.measure.name }]
    };

    await this.api.post({ url: "/api/v0.0.3/pdf/tags/remove-recommendation", data: payload });

    this.dispatch({
      type: TaggerAction.DeleteTag,
      payload: { tagId }
    });
  };

  public deleteTag: ITaggerActions["deleteTag"] = async (data) => {
    const { dealId, seriesId, tagId } = data;
    try {
      await this.api.delete({ url: `/api/v0.0.3/pdf/tags/${tagId}` });
      this.fetchTags({ dealId, ...(seriesId && { seriesId }), overrideStateTags: true });
    } catch (e) {
      console.log(e);
    }
  };

  public deleteSplitOfTags: ITaggerActions["deleteSplitOfTags"] = async (data) => {
    const { dealId, seriesId, splitId, investorId } = data;
    try {
      await this.api.delete({
        url: `/api/v0.0.3/pdf/tags/by-split-id/${splitId}`,
        params: { investor_id: investorId } //if investorId is provided - we remove child group by investor
      });
      this.fetchTags({ dealId, ...(seriesId && { seriesId }), overrideStateTags: true });
    } catch (e) {
      console.log(e);
    }
  };

  public splitTagByCommitment: ITaggerActions["splitTagByCommitment"] = (dealId, seriesId, tagId) => {
    this.api.post({ url: `/api/v0.0.3/pdf/tags/recommendations/${tagId}/split-by-commitment` }).then(() => {
      return this.fetchTags({ dealId, ...(seriesId && { seriesId }), overrideStateTags: true });
    });
  };

  public splitTagByInvestor: ITaggerActions["splitTagByInvestor"] = (dealId, seriesId, tagId, investorId) => {
    this.api
      .post({
        url: `/api/v0.0.3/pdf/tags/recommendations/${tagId}/split-by-investor`,
        data: { investor_id: investorId }
      })
      .then(() => {
        return this.fetchTags({ dealId, ...(seriesId && { seriesId }), overrideStateTags: true });
      });
  };

  public tagAsDraft: ITaggerActions["tagAsDraft"] = (params) => {
    const { dealId, seriesId, tagId, is_draft } = params;
    this.api.patch({ url: `/api/v0.0.3/pdf/tags/${tagId}`, data: { is_draft } }).then(() => {
      this.fetchTags({ dealId, ...(seriesId && { seriesId }), overrideStateTags: true });
    });
  };

  public tagSplitAsDraft: ITaggerActions["tagSplitAsDraft"] = (params) => {
    const { dealId, seriesId, splitId, is_draft } = params;
    this.api.patch({ url: `/api/v0.0.3/pdf/tags/by-split-id/${splitId}`, data: { is_draft } }).then(() => {
      this.fetchTags({ dealId, ...(seriesId && { seriesId }), overrideStateTags: true });
    });
  };

  public getDocumentPolygons = (docId: ITaggerDocument["id"]): Promise<Polygons> => {
    return this.api.get({ url: `/api/v0.0.3/pdf/doc/${docId}/polygons` }).then((res) => res.data);
  };

  public fetchTags: ITaggerActions["fetchTags"] = async (params: IFetchTagsParams): Promise<void> => {
    const { dealId, docId, seriesId, overrideStateTags } = params;

    const res = await this.api.get<AltiusRes<ITag[]>>({
      url: "/api/v0.0.3/pdf/tags/",
      params: {
        deal_id: dealId,
        document_id: docId,
        series_id: seriesId
      }
    });

    const addWordsToTags = (tags: ITag[]): IProcessedTag[] => {
      return tags.map((tag) => ({
        ...tag,
        words: tag.tag.file_polygon_id ? [{ rectId: tag.tag.file_polygon_id, text: String(tag.value) }] : [],
        children: tag.children ? addWordsToTags(tag.children) : null
      }));
    };
    const tags = addWordsToTags(res.data.data);
    if (overrideStateTags) {
      this.dispatch({ type: TaggerAction.SetTags, payload: { tags, dealId, docId, seriesId } });
    } else if (tags.length) {
      this.dispatch({ type: TaggerAction.AddTags, payload: { tags } });
    }
  };

  // public setSuspects = (docId: string, suspects: Suspect[]) => {
  //   const tags = suspects.map<Tag>((suspect, index) => ({
  //     id: Date.now() + index,
  //     tag: suspect.tag,
  //     measureId: 0,
  //     words: suspect.words,
  //     isSuspect: true
  //   }));
  //
  //   this.dispatch({ type: TaggerAction.AddTags, payload: { tags, docId } });
  // };

  public setAnalyzingStatus = (status: ITagger["analyzingStatus"]) => {
    this.dispatch({ type: TaggerAction.FilesAnalyzingStatus, payload: status });
  };

  public startAnalysis = async (docIds: number[]) => {
    this.dispatch({ type: TaggerAction.FilesAnalyzingStatus, payload: "processing" });

    try {
      const res = await this.api.post<AltiusRes<"ALL DONE"> | undefined>({
        url: "/api/v0.0.3/textract/analyze-documents",
        data: { documents: docIds }
      });

      // This result indicates that there are no documents to analyze
      if (res.data?.data === "ALL DONE") {
        this.dispatch({ type: TaggerAction.FilesAnalyzingStatus, payload: "finished" });
      }
    } catch {
      this.dispatch({ type: TaggerAction.FilesAnalyzingStatus, payload: "error" });
    }
  };

  public undoAnalysis = async (docIds: number[]) => {
    this.dispatch({ type: TaggerAction.FilesAnalyzingStatus, payload: "pending" });

    try {
      await this.api.post<AltiusRes<"ALL DONE"> | undefined>({
        url: "/api/v0.0.3/textract/undo-analyze",
        data: { documents: docIds }
      });

      await Promise.all(docIds.map((docId) => this.fetchTags({ docId })));
    } catch {
      this.dispatch({ type: TaggerAction.FilesAnalyzingStatus, payload: "error" });
    }
  };

  public resetTagger = () => {
    this.dispatch({ type: TaggerAction.ResetTagger, payload: null });
  };
}
